import { ReactNode, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useEffectOnce } from 'hooks/useEffectOnce';
import {
  CONSENT_EVENT,
  initProviders,
  loadProviders,
  readReferrers,
  readSources,
  saveReferrers,
  saveSources,
  startConsent,
} from 'tracking';
import {
  trackButtonClickFaro,
  trackCheckboxBlurFaro,
  trackCheckboxFocusFaro,
  trackEventFaro,
  trackFormSubmitErrorFaro,
  trackFormSubmitFaro,
  trackFormSubmitSuccessFaro,
  trackInputBlurFaro,
  trackInputFocusFaro,
  trackInputPasswordVisibilityClickFaro,
  trackLinkClickFaro,
  trackPageViewFaro,
  trackSelectBlurFaro,
  trackSelectFocusFaro,
} from 'utils/faro';
import { noop } from 'utils/noop';

import { defaultTrackingContextActions, defaultTrackingContextState, TrackingContext } from './trackingContext';

export interface TrackingProviderProps {
  children: ReactNode;
}

export const TrackingProvider = ({ children }: TrackingProviderProps) => {
  const [trackingState, setTrackingState] = useState(defaultTrackingContextState);
  const location = useLocation();
  // Refs instead of state so the location effect below only depends on the location itself,
  // mirroring the `history.listen` subscriptions this replaces: they never fired for the
  // location they were registered on
  const isInitialLocation = useRef(true);
  const canTrackPageViews = useRef(false);
  // OneTrust can grant consent more than once per page load (stored consent, then the banner callback)
  const hasStartedTracking = useRef(false);

  useEffect(() => {
    if (isInitialLocation.current) {
      isInitialLocation.current = false;
      return;
    }

    readSources(location.search);
    saveSources();
    readReferrers();
    saveReferrers();

    if (canTrackPageViews.current) {
      trackPageViewFaro(location.pathname);
    }
  }, [location]);

  useEffectOnce(() => {
    initProviders();
    readSources(location.search);
    readReferrers();

    // Listen before starting, as stored consent is granted synchronously
    document.addEventListener(CONSENT_EVENT, startTracking);
    startConsent();

    setTrackingState((trackingState) => ({
      ...trackingState,
      initialized: true,
    }));

    return () => document.removeEventListener(CONSENT_EVENT, startTracking);
  });

  const startTracking = () => {
    if (hasStartedTracking.current) {
      return;
    }

    hasStartedTracking.current = true;

    setTrackingState((trackingState) => ({
      ...trackingState,
      trackingData: {
        canTrack: true,
      },
    }));

    saveSources();
    saveReferrers();
    loadProviders();

    canTrackPageViews.current = true;
  };

  return (
    <TrackingContext.Provider
      value={[
        trackingState,
        {
          ...defaultTrackingContextActions,
          faro: {
            ...defaultTrackingContextActions.faro,
            trackEvent: trackingState.trackingData.canTrack ? trackEventFaro : noop,
            trackButtonClick: trackingState.trackingData.canTrack ? trackButtonClickFaro : noop,
            trackCheckboxBlur: trackingState.trackingData.canTrack ? trackCheckboxBlurFaro : noop,
            trackCheckboxFocus: trackingState.trackingData.canTrack ? trackCheckboxFocusFaro : noop,
            trackFormSubmit: trackingState.trackingData.canTrack ? trackFormSubmitFaro : noop,
            trackFormSubmitError: trackingState.trackingData.canTrack ? trackFormSubmitErrorFaro : noop,
            trackFormSubmitSuccess: trackingState.trackingData.canTrack ? trackFormSubmitSuccessFaro : noop,
            trackInputBlur: trackingState.trackingData.canTrack ? trackInputBlurFaro : noop,
            trackInputFocus: trackingState.trackingData.canTrack ? trackInputFocusFaro : noop,
            trackInputPasswordVisibilityClick: trackingState.trackingData.canTrack
              ? trackInputPasswordVisibilityClickFaro
              : noop,
            trackLinkClick: trackingState.trackingData.canTrack ? trackLinkClickFaro : noop,
            trackSelectBlur: trackingState.trackingData.canTrack ? trackSelectBlurFaro : noop,
            trackSelectFocus: trackingState.trackingData.canTrack ? trackSelectFocusFaro : noop,
          },
        },
      ]}
    >
      {trackingState.initialized ? children : null}
    </TrackingContext.Provider>
  );
};
